import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "../lib/api";
import { KpiCard } from "../components/KpiCard";
import { RankBadge } from "../components/RankBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { FileDown, Presentation as PresentationIcon } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { WorldClassScoreInfo } from "../components/WorldClassScoreInfo";

const PPTX_ROLES = ["ADMIN", "DIRECTION_SECURITE"];

export default function DashboardNational() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [campagnes, setCampagnes] = useState([]);
  const [campagneId, setCampagneId] = useState("");
  const [data, setData] = useState(null);
  const [ranking, setRanking] = useState([]);

  useEffect(() => {
    api.get("/campagnes").then((res) => {
      setCampagnes(res.data);
      const archived = res.data.find((c) => c.statut === "ARCHIVE") || res.data.find((c) => c.annee === "2024");
      const cross = res.data.find((c) => c.type_campagne === "CROSS_ASSESSMENT");
      setCampagneId((archived || cross)?.campagne_id || "");
    });
  }, []);

  useEffect(() => {
    if (campagneId) {
      api.get("/dashboard/national", { params: { campagne_id: campagneId } }).then((res) => setData(res.data));
      api.get("/dashboard/ranking", { params: { campagne_id: campagneId } }).then((res) => setRanking(res.data));
    }
  }, [campagneId]);

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-5" data-testid="dashboard-national-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-heading text-3xl font-black uppercase tracking-tight">Analyses Nationales</h1>
        <Select value={campagneId} onValueChange={setCampagneId}>
          <SelectTrigger className="w-56" data-testid="national-campagne-select"><SelectValue placeholder="Campagne" /></SelectTrigger>
          <SelectContent>{campagnes.map((c) => <SelectItem key={c.campagne_id} value={c.campagne_id}>{c.nom}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {data && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="national-kpi-grid">
          <KpiCard title="Audits réalisés / prévus" value={`${data.audits_realises}/${data.audits_prevus}`} color="slate" testId="national-kpi-audits" />
          <KpiCard title="Progression moyenne" value={`${data.progression_moyenne}%`} color="emerald" testId="national-kpi-progression" />
          <KpiCard title="Minimum Requirements" value={data.score_minimum_moyen} sub="/4" color="sky" testId="national-kpi-min" />
          <KpiCard title="World Class" value={data.score_world_class_moyen} sub="/4" color="sky" testId="national-kpi-wc" />
          <KpiCard title="Non-conformités" value={data.non_conformites} color="rose" testId="national-kpi-nc" />
          <KpiCard title="Actions ouvertes" value={data.actions_ouvertes} color="amber" testId="national-kpi-actions" />
          <KpiCard title="Actions en retard" value={data.actions_en_retard} color="rose" testId="national-kpi-retard" />
          <KpiCard title="Actions critiques" value={data.actions_critiques} color="rose" testId="national-kpi-critiques" />
        </div>
      )}

      <div className="flex gap-3 flex-wrap">
        <Button data-testid="goto-heatmap-button" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => navigate("/heatmap")}>Voir la Heatmap nationale</Button>
        <Button variant="outline" onClick={() => navigate("/historique")}>Comparaison 2024 → 2026</Button>
        {user?.role === "ADMIN" && (
          <Button
            variant="outline"
            className="gap-2"
            data-testid="download-national-report-button"
            disabled={!campagneId}
            onClick={() => window.open(`${API}/reports/national?campagne_id=${campagneId}`, "_blank")}
          >
            <FileDown className="h-4 w-4" /> Télécharger le rapport national (PDF)
          </Button>
        )}
        {PPTX_ROLES.includes(user?.role) && (
          <Button
            variant="outline"
            className="gap-2"
            data-testid="download-national-pptx-button"
            disabled={!campagneId}
            onClick={() => window.open(`${API}/reports/national/pptx?campagne_id=${campagneId}`, "_blank")}
          >
            <PresentationIcon className="h-4 w-4" /> Synthèse nationale (PPTX)
          </Button>
        )}
      </div>

      <Card data-testid="national-ranking-card">
        <CardHeader><CardTitle className="flex items-center gap-1.5">Classement World Class par site <WorldClassScoreInfo testId="ranking-world-class-info" /></CardTitle></CardHeader>
        <CardContent className="space-y-1.5">
          {ranking.length ? ranking.map((r, i) => (
            <div
              key={r.site_id}
              data-testid={`ranking-row-${r.site_id}`}
              className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm border-b py-3 last:border-0 cursor-pointer hover:bg-muted/50 rounded-md px-2"
              onClick={() => navigate(`/sites/${r.site_id}?campagne_id=${campagneId}`)}
            >
              <div className="flex items-start gap-2 min-w-0">
                <span className="font-mono text-xs text-muted-foreground w-5 shrink-0 pt-0.5">{i + 1}.</span>
                <div className="min-w-0">
                  <div className="font-medium leading-snug break-words">{r.nom_site}</div>
                  <div className="text-xs text-muted-foreground">{r.categorie}</div>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0 pl-7 sm:pl-0">
                <span className="text-xs font-mono text-muted-foreground">Min. {r.minimum_pct}%</span>
                <RankBadge rank={r.rank} worldClassPct={r.world_class_pct} testId={`ranking-badge-${r.site_id}`} />
              </div>
            </div>
          )) : <p className="text-sm text-muted-foreground">Aucune donnée de classement.</p>}
        </CardContent>
      </Card>
    </div>
  );
}
